import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import PlacementAnalyticsService from './PlacementAnalyticsService';
import CompanyLandscapeOverview, { EMPTY_LANDSCAPE, LandscapeData } from './components/CompanyLandscapeOverview';
import { type PlacementRoleBase } from './deepLinks';
import './placementAnalytics.css';

interface Props {
    instituteId: number | string;
    roleBase: PlacementRoleBase;
    instituteName?: string | null;
}

const SAMPLE_URL =
    'https://ags-qa-bucket.s3.dualstack.ap-south-1.amazonaws.com/Template_Company_Landscape.xlsx';

const PlacementInsightDashboard = ({ instituteId, roleBase, instituteName }: Props) => {
    const [data, setData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const loadDashboard = useCallback(async () => {
        const res = await PlacementAnalyticsService.getInsightDashboard(instituteId);
        setData(res.data);
    }, [instituteId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        loadDashboard()
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.response?.data?.message || e?.message || 'Failed to load Insights dashboard');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [loadDashboard]);

    const onUpload = async (e: FormEvent) => {
        e.preventDefault();
        if (!file) {
            setUploadError('Choose an .xlsx file');
            return;
        }
        setUploading(true);
        setUploadError(null);
        setMessage(null);
        try {
            const res = await PlacementAnalyticsService.upload(instituteId, file, {
                fileType: 'INSIGHT_DASHBOARD',
            });
            setMessage(
                res.data?.status === 'SUCCESS'
                    ? `Insights dashboard uploaded successfully (upload #${res.data.uploadId})`
                    : 'Upload completed'
            );
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            await loadDashboard();
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.errors?.join?.('\n') ||
                err?.message ||
                'Upload failed';
            setUploadError(String(msg));
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return <div className="text-muted font-13">Loading Insights dashboard…</div>;
    }
    if (error) {
        return <div className="alert alert-danger py-2 font-13">{error}</div>;
    }

    const landscape: LandscapeData = data?.landscape?.metrics ? data.landscape : EMPTY_LANDSCAPE;
    const uploadedAt = data?.createdAt ? new Date(data.createdAt).toLocaleString() : '';
    const canUpload = roleBase === 'support';
    const hasExisting = Boolean(data?.uploadId);

    return (
        <div>
            {canUpload && (
                <form onSubmit={onUpload} className="border rounded p-3 p-md-4 mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div>
                            <h3 className="font-18 font-600 mb-1">Upload insight Dashboard file</h3>
                            <p className="font-13 text-muted mb-0">
                                Upload the institute&apos;s <strong>Template_Company_Landscape.xlsx</strong>.
                                Re-uploading replaces the existing dashboard.
                            </p>
                            {data?.fileName && (
                                <p className="font-12 text-muted mb-0 mt-1">
                                    Current: <strong>{data.fileName}</strong>
                                    {uploadedAt ? ` · updated ${uploadedAt}` : ''}
                                </p>
                            )}
                        </div>
                        <a
                            href={SAMPLE_URL}
                            download="Template_Company_Landscape.xlsx"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary">
                            <span className="d-flex align-items-center">
                                <i className="bi bi-cloud-arrow-down-fill font-16 me-2" aria-hidden="true" />
                                Download Sample File
                            </span>
                        </a>
                    </div>
                    <div className="row g-3 mt-1 align-items-end">
                        <div className="col-12">
                            <label className="form-label font-13 mb-1">Choose file</label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                className="form-control font-13"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                disabled={uploading}
                            />
                        </div>
                        <div className="col-12">
                            <button
                                type="submit"
                                className="btn btn-green"
                                disabled={uploading || !file}>
                                <span>{uploading ? 'Uploading…' : hasExisting ? 'Re-upload' : 'Upload'}</span>
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {message && <div className="alert alert-success py-2 font-13">{message}</div>}
            {uploadError && (
                <div className="alert alert-danger py-2 font-13" style={{ whiteSpace: 'pre-wrap' }}>
                    {uploadError}
                </div>
            )}

            <CompanyLandscapeOverview landscape={landscape} instituteName={instituteName} />
        </div>
    );
};

export default PlacementInsightDashboard;
